import React, {useContext, useState} from "react";
import IconFullStar from "../../icon/IconFullStar";
import Tooltip from "../../../ui/Tooltip";
import IconInfo from "../../icon/IconInfo";
import {ConsentForTestimonial, Evaluation, Maybe} from "../../../schema";
import EvaluationReply from "./EvaluationReply";
import PrimaryButton from "../../../ui/PrimaryButton";
import SecondaryButton from "../../../ui/SecondaryButton";
import IconTrash from "../../icon/IconTrash";
import {showError, showInfo} from "../../../ui/ToastNotification";
import {GraphQLError} from "graphql";
import {gql, useMutation} from "@apollo/client";
import IconCheck from "../../icon/IconCheck";
import DeleteEvaluationDlg from "./DeleteEvaluationDlg";
import Testimonial from "./Testimonial";
import {AttendeeScreenData} from "../../../scenes/app/attendees/AttendeeDetailScene";
import cn from "classnames";
import Tip, {TipType} from "../../../ui/Tip";
import If from "../../utils/If";
import UILink from "../../../ui/UILink";
import {AccountContext} from "../../../utils/Context";

type Props = {
  eventTitle: string;
  attendee: AttendeeScreenData;
  evaluation: Maybe<Evaluation> | undefined
}

export default function EvaluationTab({evaluation, eventTitle, attendee}: Props) {

  const [approveEvaluation, {loading}] = useMutation(approveMutation);
  const [removeTestimonial] = useMutation(removeMutation);
  const [createTestimonial, {loading: createLoading}] = useMutation(createMutation);
  const [showDeleteDlg, setShowDeleteDlg] = useState(false);
  const account = useContext(AccountContext);

  const onApprove = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    approveEvaluation({variables: {id: evaluation?.id}}).then(
      () => {
        showInfo('Evaluation was approved');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      },
    );
  }, [approveEvaluation, evaluation]);

  const onCreateTestimonial = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    createTestimonial({variables: {id: evaluation?.id}}).then(
      () => {
        showInfo('Testimonial was added');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      },
    );
  }, [createTestimonial, evaluation]);

  const onRemoveTestimonial = React.useCallback(() => {
    removeTestimonial({variables: {id: evaluation?.id}}).then(
      () => {
        showInfo('Testimonial was removed');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((error: GraphQLError) => showError(error.message));
      },
    );
  }, [removeTestimonial, evaluation]);


  return (
    <>
      <div className="flex flex-row justify-between items-center mt-6">
        <h3 className="text-2xl font-bold">Evaluation: <span>
          {!evaluation && "None"}
          {evaluation &&
            <>{evaluation.rating} <IconFullStar
              className={cn({"text-yellow": evaluation.approved, "text-gray": !evaluation.approved})}/>
              <Tooltip
                content={<div className='w-64'>This rating will be taken into account when calculating your rating as a
                  trainer</div>}
                label={`evaluation_label_tip`}
                className="pl-0.5">
                <IconInfo className="text-gray text-base mb-0.5"/>
              </Tooltip>
            </>
          }
          </span>
        </h3>
        <div>
          {evaluation &&
            <>
              {!evaluation?.approved && <PrimaryButton className="mr-2" loading={loading}
                                                       onClick={(e: React.MouseEvent<HTMLButtonElement>) => onApprove(e)}><IconCheck
                className="mr-1 -mt-0.5"/> Approve</PrimaryButton>}
              <SecondaryButton onClick={() => setShowDeleteDlg(true)}><IconTrash
                className="mr-1 -mt-0.5"/> Delete</SecondaryButton>
            </>
          }
        </div>
      </div>
      {evaluation &&
        <div className="overflow-hidden py-5 pt-3 grid grid-cols-3 gap-x-4">
          <div className="col-span-2 mt-8">
            {evaluation.content && evaluation.content.map((section) =>
              <div key={section.label}>
                {section.replies.length > 0 &&
                  <div key={section.label} className="mb-8">
                    <div className="text-xl mb-2">{section.label}</div>
                    <div className="bg-gray-light px-4 rounded-xl">
                      {section.replies.map((reply, index) =>
                        <EvaluationReply reply={reply} index={index} key={reply.fieldId}/>
                      )}
                    </div>
                  </div>
                }
              </div>
            )}
          </div>
          <div className="mt-12">
            {account && (account.isTrainer()||account.isCompany()) &&
              <>
                {
                  evaluation.approved &&
                  <>
                    <Testimonial name={attendee.name} company={attendee.company || undefined}
                                 role={attendee.companyRole || undefined}
                                 rating={evaluation.rating} eventTitle={eventTitle} content={evaluation.review}/>
                    <If condition={!evaluation.testimonial}>
                      <>
                        <If condition={evaluation.consent === ConsentForTestimonial.NotGiven}>
                          <Tip type={TipType.Error}>{attendee.name} did not give permission to use their feedback for
                            marketing purposes. You cannot convert this review to testimonial.</Tip>
                        </If>
                        <If condition={evaluation.consent === ConsentForTestimonial.Unknown}>
                          <Tip type={TipType.Info}>{attendee.name} did not give explicit permission to use their
                            feedback
                            for
                            marketing purposes - there was no consent field on the feedback form. Make sure
                            you have the permission to use this review publicly.</Tip>
                        </If>
                        <If condition={evaluation.consent === ConsentForTestimonial.Given}>
                          <Tip type={TipType.Success}>{attendee.name} gave permission to use their feedback for
                            marketing
                            purposes.</Tip>
                        </If>
                        <If condition={evaluation.consent !== ConsentForTestimonial.NotGiven}>
                          <PrimaryButton className="mt-4 !py-1" loading={loading}
                                         onClick={(e) => onCreateTestimonial(e)}>
                            Convert to testimonial</PrimaryButton>
                        </If>
                      </>
                    </If>
                    <If condition={evaluation.testimonial}>
                      <div className="px-6 py-4 rounded-md flex flex-col text-gray-dark bg-yellow-light">
                    <span>Great job! This review has been converted to testimonial. Go to your public trainer profile to see
                    how it looks like.</span>
                        <UILink href="#" className="mt-4" onClick={onRemoveTestimonial}>Remove</UILink>
                      </div>
                    </If>
                  </>
                }
                {!evaluation.approved &&
                  <Tip type={TipType.Advice} className="mt-4">
                    As soon as you approve the evaluation, this message is replaced by a testimonial which will appear
                    on your trainer profile if you give your permission.
                  </Tip>
                }
              </>
            }
          </div>
        </div>
      }
      {evaluation &&
        <DeleteEvaluationDlg id={evaluation?.id} show={showDeleteDlg} setShowDialog={setShowDeleteDlg}/>
      }
    </>
  );
}

const approveMutation = gql`
  mutation ApproveEvaluation($id: ID!) {
    approveEvaluation(evaluationId: $id) {
      id
      approved
    }
  }
`;

const createMutation = gql`
  mutation CreateTestimonial($id: ID!) {
    createTestimonial(evaluationId: $id) {
      id
      testimonial
    }
  }
`;

const removeMutation = gql`
  mutation RemoveTestimonial($id: ID!) {
    removeTestimonial(evaluationId: $id) {
      id
      testimonial
    }
  }
`;
